import { useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { captureException } from '@sentry/react';
import { TbBuilding } from 'react-icons/tb';

import type { MetricChartCompany } from '@/shared/components/data-visualization/recharts';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import {
    DEFAULT_FINANCIAL_COMPANIES,
    enrichFinancialCompanies,
    financialCompanyLabel,
    searchEnrichedFinancialCompanies,
} from '@/shared/components/financial-statements/financialCompanySearch';
import TickerExchangeTag from '@/shared/components/tags/TickerExchangeTag';
import { corporateLogo } from '@/utilities';

import PlatformSelect from '../PlatformSelect';

interface FinancialCompanyOption {
    company: MetricChartCompany;
    label: string;
    value: string;
}

export interface FinancialCompanySelectProps {
    disabled?: boolean;
    multiple?: boolean;
    onChange?: (value: string | string[]) => void;
    placeholder?: string;
    value?: string | string[];
}

const dedupeCompanies = (companies: MetricChartCompany[]) => {
    const companiesBySymbol = new Map<string, MetricChartCompany>();

    companies.forEach((company) => {
        if (company.symbol) {
            companiesBySymbol.set(company.symbol, { ...companiesBySymbol.get(company.symbol), ...company });
        }
    });

    return [...companiesBySymbol.values()];
};

const FinancialCompanySelect: FC<FinancialCompanySelectProps> = ({
    disabled,
    multiple = false,
    onChange,
    placeholder = 'Search Companies By Name Or Ticker...',
    value,
}) => {
    const [defaults, setDefaults] = useState(DEFAULT_FINANCIAL_COMPANIES);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<MetricChartCompany[]>([]);
    const searchControllerRef = useRef<AbortController>();
    const searchTimerRef = useRef<number>();

    useEffect(() => {
        const controller = new AbortController();

        const enrichDefaults = async () => {
            try {
                setDefaults(await enrichFinancialCompanies(DEFAULT_FINANCIAL_COMPANIES, controller.signal));
            } catch (error) {
                if (!controller.signal.aborted) {
                    captureException(error, { tags: { feature: 'financial-company-select-defaults' } });
                }
            }
        };

        void enrichDefaults();

        return () => controller.abort();
    }, []);

    useEffect(
        () => () => {
            if (searchTimerRef.current) {
                window.clearTimeout(searchTimerRef.current);
            }
            searchControllerRef.current?.abort();
        },
        [],
    );

    const handleSearch = (input: string) => {
        const query = input.trim();

        if (searchTimerRef.current) {
            window.clearTimeout(searchTimerRef.current);
        }
        searchControllerRef.current?.abort();

        if (query.length < 2) {
            setLoading(false);
            setResults([]);
            return;
        }

        const controller = new AbortController();
        searchControllerRef.current = controller;
        setLoading(true);
        searchTimerRef.current = window.setTimeout(async () => {
            try {
                setResults(await searchEnrichedFinancialCompanies(query, controller.signal));
            } catch (error) {
                if (!controller.signal.aborted) {
                    captureException(error, { tags: { feature: 'financial-company-select-search' } });
                    setResults([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        }, 250);
    };

    const selectedSymbols = useMemo(() => (Array.isArray(value) ? value : value ? [value] : []), [value]);
    const companies = useMemo(
        () =>
            dedupeCompanies([
                ...selectedSymbols.map((symbol) => ({ companyName: symbol, symbol })),
                ...results,
                ...defaults,
            ]),
        [defaults, results, selectedSymbols],
    );
    const options = useMemo<FinancialCompanyOption[]>(
        () =>
            companies.map((company) => ({
                company,
                label: financialCompanyLabel(company),
                value: company.symbol,
            })),
        [companies],
    );

    return (
        <PlatformSelect<string | string[], FinancialCompanyOption>
            disabled={disabled}
            filterOption={false}
            loading={loading}
            mode={multiple ? 'multiple' : undefined}
            onChange={(nextValue) => onChange?.(nextValue)}
            onSearch={handleSearch}
            optionLabelProp='label'
            optionRender={(option) => {
                const company = option.data.company;

                return (
                    <DropdownOptionContent
                        avatar={{
                            icon: <TbBuilding />,
                            props: { size: 'small' },
                            src: company.website ? corporateLogo(company.website) : company.image,
                        }}
                        metadata={[
                            <TickerExchangeTag
                                companyName={company.companyName}
                                compact
                                exchange={company.exchange}
                                key='ticker'
                                symbol={company.symbol}
                                website={company.website}
                            />,
                            company.sector,
                        ]}
                        title={company.companyName ?? company.symbol}
                    />
                );
            }}
            options={options}
            placeholder={placeholder}
            showSearch
            style={{ width: '100%' }}
            value={value}
        />
    );
};

export default FinancialCompanySelect;
